import {Button} from '@dropbox/dig-components/buttons';
import {Box} from '@dropbox/dig-foundations';
import type {WorkstreamUpdate} from 'client';
import {isLexicalJsonString, toPlainTextLexicalJson} from 'components/DSYS/editor/utils/lexical';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {useCallback, useRef, useState} from 'react';

import {logWorkstreamsInteraction} from './workstreamsAnalytics';

// Short, since long updates (the ones that clamp) lead with an AI summary;
// this keeps Metrics / Milestones in view. "Show full update" has the rest.
const CLAMP_LINES = 4;

/**
 * /workstreams: a read-only update, clamped to `lines` (CLAMP_LINES
 * by default) with a toggle when it runs longer. Width is left to the caller.
 */
export const ClampedUpdate = ({
  update,
  lines = CLAMP_LINES,
}: {
  update: WorkstreamUpdate;
  lines?: number;
}) => {
  const [showAll, setShowAll] = useState(false);
  const [overflows, setOverflows] = useState(false);
  // Unclamped, the editor reports "not truncated"; keep the toggle regardless.
  const showAllRef = useRef(showAll);
  showAllRef.current = showAll;
  const handleOverflow = useCallback((truncated: boolean) => {
    if (!showAllRef.current) setOverflows(truncated);
  }, []);

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start" style={{gap: 4}}>
      <Box style={{width: '100%'}}>
        <RichTextArea
          editable={false}
          value={
            isLexicalJsonString(update.comment)
              ? update.comment
              : toPlainTextLexicalJson(update.comment)
          }
          truncateLines={showAll ? undefined : lines}
          onTruncateOverflowChange={handleOverflow}
          squareBorders
        />
      </Box>
      {(overflows || showAll) && (
        <Button
          variant="transparent"
          size="small"
          onClick={() => {
            logWorkstreamsInteraction('full_update_toggled', {
              workstream_id: update.workstream_id,
              expanded: !showAll,
            });
            setShowAll(!showAll);
          }}
        >
          {showAll ? 'Show less' : 'Show full update'}
        </Button>
      )}
    </Box>
  );
};
